import React, { useEffect, useState } from 'react';
import {
  ScrollView,
  View,
  Text,
  TouchableHighlight,
  TouchableOpacity,
  Dimensions,
  TextInput,
  ActivityIndicator,
} from 'react-native';
import { FontAwesome5 } from '@expo/vector-icons';

import { getCustomersData, deleteCustomer } from '../../../api/Customer';
import { msgStr } from '../../../common/constants/Message';
import { TextMediumSize } from '../../../common/constants/Fonts';
import { useAlertModal, useConfirmModal } from '../../../common/hooks';
import BasicLayout from '../../../common/components/CustomLayout/BasicLayout';

import { customersStyle } from './styles/CustomersStyle';
import AddCustomerModal from './AddCustomerModal';
import OpenStoreModal from './OpenStoreModal';
import ResetPasswordModal from './ResetPasswordModal';

const PAGE_SIZE = 25;

const Customers = ({ navigation }) => {
  const screenHeight = Dimensions.get('window').height;

  const { showAlert } = useAlertModal();
  const { showConfirm } = useConfirmModal();

  const [tableData, setTableData] = useState([]);
  const [updateCustomerTrigger, setUpdateCustomerTrigger] = useState(true);

  const [isAddModalVisible, setAddModalVisible] = useState(false);
  const [isOpenStoreModalVisible, setOpenStoreModalVisible] = useState(false);
  const [isResetPassModalVisible, setResetPassModalVisible] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [searchKey, setSearchKey] = useState('');

  // Server-side pagination
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);

  const openAddCustomerModal = () => {
    setAddModalVisible(true);
    setSelectedCustomer(null);
  };
  const closeAddCustomerModal = () => {
    setAddModalVisible(false);
    setSelectedCustomer(null);
  };

  const openStoreModal = () => {
    setOpenStoreModalVisible(true);
  };
  const closeStoreModal = () => {
    setSelectedCustomer(null);
    setOpenStoreModalVisible(false);
  };

  const openResetPassModal = (customer: any) => {
    setSelectedCustomer(customer);
    setResetPassModalVisible(true);
  };
  const closeResetPassModal = () => {
    setSelectedCustomer(null);
    setResetPassModalVisible(false);
  };

  const editCustomer = (customer) => {
    setSelectedCustomer(customer);
    setAddModalVisible(true);
  };

  // Typing in search resets to page 1; the debounced effect below fetches.
  const onChangeSearch = (text: string) => {
    setSearchKey(text);
    setPage(1);
  };

  const getTable = () => {
    setLoading(true);
    getCustomersData({ page, limit: PAGE_SIZE, search: searchKey.trim() }, (jsonRes, status, error) => {
      setLoading(false);
      if (updateCustomerTrigger) setUpdateCustomerTrigger(false);
      switch (status) {
        case 200: {
          // New paginated shape { data, pagination }; tolerate a bare array too.
          const rows = Array.isArray(jsonRes) ? jsonRes : jsonRes?.data || [];
          setTableData(rows);
          if (jsonRes?.pagination) {
            setTotal(jsonRes.pagination.total ?? rows.length);
            setTotalPages(jsonRes.pagination.total_pages ?? 1);
          } else {
            setTotal(rows.length);
            setTotalPages(1);
          }
          break;
        }
        case 500:
          showAlert('error', msgStr('serverError'));
          break;
        default:
          if (jsonRes && jsonRes.error) showAlert('error', jsonRes.error);
          else showAlert('error', msgStr('unknownError'));
          break;
      }
    });
  };

  // Debounced server fetch on page / search / external refresh changes.
  useEffect(() => {
    const timer = setTimeout(() => getTable(), 350);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, searchKey, updateCustomerTrigger]);

  const removeCustomer = (id) => {
    showConfirm(msgStr('deleteConfirmStr'), () => {
      deleteCustomer(id, (jsonRes, status, error) => {
        switch (status) {
          case 200:
            setUpdateCustomerTrigger(true);
            showAlert('success', jsonRes.message);
            break;
          default:
            if (jsonRes && jsonRes.error) showAlert('error', jsonRes.error);
            else showAlert('error', msgStr('unknownError'));
            break;
        }
      });
    });
  };

  const isDeactivated = (item: any) =>
    item?.account_deactivated === true ||
    item?.account_deactivated === 1 ||
    item?.account_deactivated === '1';

  const renderTableData = () => {
    const rows = [];
    tableData.map((item, index) => {
      const deactivated = isDeactivated(item);
      rows.push(
        <View key={index} style={styles.tableRow}>
          <View style={[styles.cell, { width: 200 }]}>
            <Text>
              {item.createdAt
                ? new Date(item.createdAt).toLocaleString('en-US', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: true,
                  })
                : ''}
            </Text>
          </View>
          <View style={styles.cell}>
            <Text>{item.first_name}</Text>
          </View>
          <View style={styles.cell}>
            <Text>{item.last_name}</Text>
          </View>
          <View style={[styles.cell, { width: 200 }]}>
            <Text>{item.email}</Text>
          </View>
          <View style={[styles.cell]}>
            <Text>{item.phone_number}</Text>
          </View>
          <View style={[styles.cell]}>
            <Text>{item.mobile_phone}</Text>
          </View>
          <View style={[styles.cell]}>
            <Text>{item?.country?.country ?? ''}</Text>
          </View>
          <View style={[styles.cell]}>
            <Text>{item?.home_location_tbl?.location}</Text>
          </View>
          <View style={[styles.cell, { width: 160 }]}>
            <Text style={{ color: deactivated ? '#c0392b' : '#1e8449', fontWeight: 'bold' }}>
              {deactivated ? 'Deactivated' : 'Active'}
            </Text>
            {deactivated && !!item.account_deactivated_at && (
              <Text style={{ fontSize: 11, color: '#888' }}>
                {new Date(item.account_deactivated_at).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: '2-digit',
                  day: '2-digit',
                })}
              </Text>
            )}
          </View>
          <View style={[styles.IconCell, { width: 100 }]}>
            <TouchableOpacity
              onPress={() => {
                setSelectedCustomer(item);
                openStoreModal();
              }}
            >
              <FontAwesome5 size={TextMediumSize} name="store" color="black" />
            </TouchableOpacity>
          </View>
          <View style={[styles.IconCell]}>
            <TouchableOpacity
              onPress={() => {
                navigation.navigate('Reservation', { selectedItem: 'Create Reservations', customerId: item.id });
              }}
            >
              <FontAwesome5 size={TextMediumSize} name="cart-plus" color="black" />
            </TouchableOpacity>
          </View>
          <View style={[styles.IconCell]}>
            <TouchableOpacity
              onPress={() => {
                openResetPassModal(item);
              }}
            >
              <FontAwesome5 size={TextMediumSize} name="key" color="black" />
            </TouchableOpacity>
          </View>
          <View style={[styles.IconCell]}>
            <TouchableOpacity
              onPress={() => {
                editCustomer(item);
              }}
            >
              <FontAwesome5 size={TextMediumSize} name="edit" color="black" />
            </TouchableOpacity>
          </View>
          <View style={[styles.IconCell]}>
            <TouchableOpacity
              onPress={() => {
                removeCustomer(item.id);
              }}
            >
              <FontAwesome5 size={TextMediumSize} name="times" color="black" />
            </TouchableOpacity>
          </View>
        </View>,
      );
    });
    return <>{rows}</>;
  };

  const fromRow = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const toRow = Math.min(page * PAGE_SIZE, total);

  return (
    <BasicLayout navigation={navigation} screenName={'Customers'}>
      <ScrollView horizontal={true}>
        <View style={styles.container}>
          <View style={styles.toolbar}>
            <TouchableHighlight style={styles.button} onPress={openAddCustomerModal}>
              <Text style={styles.buttonText}>Add</Text>
            </TouchableHighlight>
            <View style={styles.searchBox}>
              <Text style={styles.searchLabel}>Search</Text>
              <TextInput
                style={styles.searchInput}
                placeholder=""
                value={searchKey}
                onChangeText={onChangeSearch}
              />
            </View>
            {loading && <ActivityIndicator style={{ marginLeft: 12 }} color="#2e96e1" />}
          </View>
          <View style={styles.tableContainer}>
            <View style={styles.tableHeader}>
              <Text style={[styles.columnHeader, { width: 200 }]}>{'Created'}</Text>
              <Text style={[styles.columnHeader]}>{'First name'}</Text>
              <Text style={[styles.columnHeader]}>{'Last name'}</Text>
              <Text style={[styles.columnHeader, { width: 200 }]}>{'Email'}</Text>
              <Text style={[styles.columnHeader]}>{'Phone number'}</Text>
              <Text style={[styles.columnHeader]}>{'Mobile number'}</Text>
              <Text style={[styles.columnHeader]}>{'Country'}</Text>
              <Text style={[styles.columnHeader]}>{'Location'}</Text>
              <Text style={[styles.columnHeader, { width: 160 }]}>{'Status'}</Text>
              <Text style={[styles.columnHeader, styles.IconCell, { width: 100 }]}>{'Login Store'}</Text>
              <Text style={[styles.columnHeader, styles.IconCell]}>{'Reserve'}</Text>
              <Text style={[styles.columnHeader, styles.IconCell]}>{'Reset Pass'}</Text>
              <Text style={[styles.columnHeader, styles.IconCell]}>{'Edit'}</Text>
              <Text style={[styles.columnHeader, styles.IconCell]}>{'DEL'}</Text>
            </View>
            <ScrollView style={{ flex: 1, maxHeight: screenHeight - 260 }}>
              {tableData.length > 0 ? (
                renderTableData()
              ) : (
                <View style={[styles.tableRow, { padding: 16 }]}>
                  <Text>{loading ? 'Loading customers…' : 'No customers found.'}</Text>
                </View>
              )}
            </ScrollView>
          </View>

          {/* Pagination controls */}
          <View style={styles.pagination}>
            <Text style={styles.paginationInfo}>
              {total === 0 ? '0 customers' : `${fromRow}–${toRow} of ${total}`}
            </Text>
            <TouchableHighlight
              style={[styles.pageButton, page <= 1 && styles.pageButtonDisabled]}
              disabled={page <= 1 || loading}
              onPress={() => setPage((p) => Math.max(1, p - 1))}
            >
              <Text style={styles.pageButtonText}>Prev</Text>
            </TouchableHighlight>
            <Text style={styles.paginationInfo}>{`Page ${page} of ${totalPages}`}</Text>
            <TouchableHighlight
              style={[styles.pageButton, page >= totalPages && styles.pageButtonDisabled]}
              disabled={page >= totalPages || loading}
              onPress={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              <Text style={styles.pageButtonText}>Next</Text>
            </TouchableHighlight>
          </View>
        </View>
      </ScrollView>

      <AddCustomerModal
        isModalVisible={isAddModalVisible}
        Customer={selectedCustomer}
        setUpdateCustomerTrigger={setUpdateCustomerTrigger}
        closeModal={closeAddCustomerModal}
      />

      <OpenStoreModal
        isModalVisible={isOpenStoreModalVisible}
        customer={selectedCustomer}
        closeModal={closeStoreModal}
      />

      <ResetPasswordModal
        isModalVisible={isResetPassModalVisible}
        customer={selectedCustomer}
        closeModal={closeResetPassModal}
      />
    </BasicLayout>
  );
};

const styles = customersStyle;

export default Customers;
